// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngZone from "@conklin/App/Components/Common/XngZone";
import { pluralize } from "@conklin/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => {
    const pagination = getProductListDataViewProperty(state, "pagination");
    const correctedQuery = getProductListDataViewProperty(state, "correctedQuery");
    const { productFilters } = state.pages.productList;

    return {
        totalItemCount: pagination?.totalItemCount,
        query: correctedQuery || productFilters.query,
        // XNG-Changes
        isLoading: state.pages.productList.isLoading,
    };
};
// XNG-Change: Has category
type Props = ReturnType<typeof mapStateToProps> & OwnProps & HasCategoryContext;

export interface ProductListProductCountStyles {
    wrapper?: InjectableCss;
    countText?: TypographyPresentationProps;
    itemsText?: TypographyPresentationProps;
    queryText?: TypographyPresentationProps;
    // XNG-Changes
    textWrapper?: TypographyPresentationProps;
    resultsForText?: TypographyPresentationProps;
    content?: InjectableCss;
}

export const productCountStyles: ProductListProductCountStyles = {
    wrapper: {
        css: css`
            margin-top: 10px;
        `,
    },
    countText: {
        css: css`
            white-space: nowrap;
        `,
    },
    queryText: {
        css: css`
            white-space: nowrap;
            font-weight: bold;
        `,
    },
};

const styles = productCountStyles;

const ProductListProductCount: FC<Props> = ({ totalItemCount, query, category, isLoading, id }) => {
    if (isLoading) {
        return null;
    }

    // XNG-Changes: remove OOTB code
    // const itemsText = (totalItemCount !== 1 ? " Products" : " Product") + (query ? " for " : "");
    // XNG-Changes: structure
    return (
        <StyledWrapper {...styles.wrapper}>
            {query && (
                <StyledWrapper {...styles.textWrapper}>
                    <Typography {...styles.resultsForText}>{translate("Results For")}</Typography>
                    <Typography {...styles.queryText} data-test-selector="productListQueryText">
                        {query}
                    </Typography>
                </StyledWrapper>
            )}
            {!query && category && (
                <Typography {...styles.queryText} data-test-selector={`categoryName_${category.name}`}>
                    {category.shortDescription ?? category.name}
                </Typography>
            )}
            <StyledWrapper {...styles.content}>
                <XngZone zoneName="Content00" hint="Category Description" contentId={id} />
            </StyledWrapper>
            <Typography {...styles.countText} data-test-selector="productListCountText">
                {pluralize(totalItemCount || 0, "Product", { showNumber: true })}
            </Typography>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withCategory(ProductListProductCount)),
    definition: {
        group: "Product List",
        displayName: "Product List Count",
        // XNG-changes: add CategoryDetailsPage
        allowedContexts: ["ProductListPage", "CategoryDetailsPage"],
    },
};

export default widgetModule;
